import { differenceInCalendarDays, format, isToday, parseISO } from 'date-fns';

import { cn } from '@shared/lib/utils';

import type { DashboardTestHistoryEntry } from '../model/types';

interface RecentTestCardProps extends DashboardTestHistoryEntry {
  isLast?: boolean;
}

const recentTestKindTitle: Record<DashboardTestHistoryEntry['kind'], string> = {
  full: 'Full exam',
  custom: 'Custom test',
  targeted: 'Targeted practice',
};

const recentTestStatusMeta: Record<
  DashboardTestHistoryEntry['status'],
  {
    label: string;
    className: string;
  }
> = {
  completed: {
    label: 'Completed',
    className: 'text-lime-600',
  },
  cancelled: {
    label: 'Cancelled',
    className: 'text-rose-500',
  },
  active: {
    label: 'In progress',
    className: 'text-muted-foreground',
  },
};

const formatRecentTestDate = (value: string) => {
  const parsedDate = parseISO(value);

  if (Number.isNaN(parsedDate.getTime())) {
    return value;
  }

  if (isToday(parsedDate)) {
    return 'Today';
  }

  const dayDifference = differenceInCalendarDays(new Date(), parsedDate);

  if (dayDifference > 0 && dayDifference < 7) {
    return `${String(dayDifference)}d ago`;
  }

  return format(parsedDate, 'dd MMM yyyy');
};

export const RecentTestCard = ({
  kind,
  status,
  sections: _sections,
  date,
  result,
  isLast = false,
}: RecentTestCardProps) => {
  const dateLabel = formatRecentTestDate(date);
  const statusMeta = recentTestStatusMeta[status];
  const title = recentTestKindTitle[kind];

  return (
    <li
      className={cn(
        'group/result flex items-center justify-between px-4 py-2.5 bg-transparent hover:bg-accent transition-colors duration-150',
        !isLast && 'border-b border-border/60'
      )}
    >
      <div className="flex flex-col gap-1">
        <span className="text-sm/none text-foreground">{title}</span>
        <div className="flex items-center gap-2 text-xs leading-none">
          <span className={cn('font-medium', statusMeta.className)}>{statusMeta.label}</span>
          <span className="text-muted-foreground">{dateLabel}</span>
        </div>
      </div>

      <div className="flex flex-col items-baseline leading-tight">
        <span
          className={cn(
            'text-base/none font-medium',
            result ? 'text-foreground' : 'text-muted-foreground'
          )}
        >
          {result ? result.point : 'N/A'}
        </span>
        <span className="text-xs text-muted-foreground">
          {result ? `${String(result.percent)}%` : '—'}
        </span>
      </div>
    </li>
  );
};
